import React from 'react'
import styles from './index.module.scss'
import { CommonMsgNodeProps } from '@/types/message'
import markdownit from 'markdown-it'
import { DownOutlined } from '@ant-design/icons'
import { $t } from '@/i18n/intl'

const md = markdownit({ html: true, breaks: true })

interface ThinkProps extends CommonMsgNodeProps {
  node: {
    value: {
      text: string
      disabled?: boolean
      status: number // 1 生成中 2 生成完成
    }
  }
}

const Think = (props: ThinkProps) => {
  const { node } = props
  // Mock 数据用于测试 - 长文本（不重复）
  // const mockText = `Breaking down the product URL to obtain core information, Product Title: Snoopy Cotton Loose Short-Sleeve T-Shirt for Men and Women, Description: 100% pure cotton, does not shrink, breathable. User Pain Points: fear of heat, deformation after a few wears, Scenarios: commuting/daily wear. Target Audience: all demographics aged 18-60. Breaking down the product URL to extract essential information, Product Title: Snoopy Cotton Loose Short-Sleeve T-Shirt for Men and Women, Description: 100% pure cotton, does not shrink, breathable. User Pain Points: fear of heat, deformation after a few wears, Scenarios: commuting/daily wear. Target Audience: all demographics aged 18-60.`

  const { text, status = 1 } = node?.value || {}
  const [isExpanded, setIsExpanded] = React.useState(false)
  const [showViewMore, setShowViewMore] = React.useState(false)
  const contentRef = React.useRef<HTMLDivElement>(null)
  const markdownRef = React.useRef<HTMLDivElement>(null)

  const statusText = status === 1 ? $t('demo.think.generating') : $t('demo.think.completedThought')

  // 检测内容是否超过4行，并在未展开时滚动到底部显示最新内容
  React.useEffect(() => {
    if (markdownRef.current && text) {
      const element = markdownRef.current
      // 临时移除高度限制来测量实际高度
      const originalMaxHeight = element.style.maxHeight
      const originalOverflow = element.style.overflow
      element.style.maxHeight = 'none'
      element.style.overflow = 'visible'
      const actualHeight = element.scrollHeight
      element.style.maxHeight = originalMaxHeight
      element.style.overflow = originalOverflow

      // 4行约等于 116px (13px * 1.69 * 4 ≈ 88px，加上一些padding)
      const maxHeight = 116
      const shouldShowViewMore = actualHeight > maxHeight
      setShowViewMore(shouldShowViewMore)

      // 如果内容超过4行且未展开，滚动到底部显示最新内容
      if (shouldShowViewMore && !isExpanded) {
        requestAnimationFrame(() => {
          if (markdownRef.current) {
            markdownRef.current.scrollTop = markdownRef.current.scrollHeight
          }
        })
      }
    }
  }, [text, isExpanded])

  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded)
  }

  return (
    <div className={styles.container}>
      <div className={styles.line}></div>
      <div className={styles.content}>
        <div className={styles.generatingBox}>{statusText}</div>
        <div className={styles.markdownWrapper} ref={contentRef}>
          <div
            ref={markdownRef}
            className={`${styles.markdownContent} ${isExpanded ? styles.expanded : ''}`}
            dangerouslySetInnerHTML={{ __html: md.render(text) }}
          />
          {!isExpanded && showViewMore && text && (
            <>
              <div className={styles.gradientMask}></div>
              <div
                className={`${styles.viewMoreButton} ${styles.absolute}`}
                onClick={handleToggleExpand}
              >
                <span className={styles.viewMoreText}>{$t('demo.think.viewMore')}</span>
                <DownOutlined className={styles.viewMoreIcon} />
              </div>
            </>
          )}
          {isExpanded && showViewMore && (
            <div className={styles.viewMoreButton} onClick={handleToggleExpand}>
              <span className={styles.viewMoreText}>{$t('demo.think.viewLess')}</span>
              <DownOutlined className={`${styles.viewMoreIcon} ${styles.rotated}`} />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default Think
